import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import useAuth from "./useAuth";

interface Ambassador {
  id: string;
  user_id: string;
  name: string;
  email: string;
  commission_rate: number;
  is_active: boolean;
  total_earnings: number;
  created_at: string;
}

interface ReferralCode {
  id: string;
  code: string;
  ambassador_id: string;
  is_active: boolean;
  uses_count: number;
  created_at: string;
}

interface Referral {
  id: string;
  referral_code_id: string | null;
  ambassador_id: string;
  referred_user_id: string;
  commission_amount: number;
  paid_out: boolean;
  created_at: string;
}

interface PrizeDrawEntry {
  id: string;
  buyer_id: string;
  ambassador_id: string;
  amount: number;
  entries: number;
  ambassador_share: number;
  platform_share: number;
  status: string;
  stripe_payment_intent_id: string | null;
  created_at: string;
}

interface AmbassadorStats {
  totalReferrals: number;
  totalEarnings: number;
  paidOut: number;
  pending: number;
  totalPrizeEntries: number;
  prizeCommission: number;
}

export function useAmbassador() {
  const { user } = useAuth();
  const [ambassador, setAmbassador] = useState<Ambassador | null>(null);
  const [referralCodes, setReferralCodes] = useState<ReferralCode[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [prizeEntries, setPrizeEntries] = useState<PrizeDrawEntry[]>([]);
  const [stats, setStats] = useState<AmbassadorStats>({
    totalReferrals: 0,
    totalEarnings: 0,
    paidOut: 0,
    pending: 0,
    totalPrizeEntries: 0,
    prizeCommission: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isAmbassador, setIsAmbassador] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setIsAmbassador(false);
      return;
    }

    fetchAmbassadorData();
  }, [user]);

  const fetchAmbassadorData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Fetch ambassador record
      const { data: ambassadorData, error: ambassadorError } = await supabase
        .from("ambassadors")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (ambassadorError) throw ambassadorError;

      if (!ambassadorData) {
        setIsAmbassador(false);
        setLoading(false);
        return;
      }

      setAmbassador(ambassadorData as Ambassador);
      setIsAmbassador(true);

      // Fetch referral codes
      const { data: codesData, error: codesError } = await supabase
        .from("referral_codes")
        .select("*")
        .eq("ambassador_id", ambassadorData.id)
        .order("created_at", { ascending: false });

      if (codesError) throw codesError;
      setReferralCodes((codesData || []) as ReferralCode[]);

      // Fetch referrals
      const { data: referralsData, error: referralsError } = await supabase
        .from("referrals")
        .select("*")
        .eq("ambassador_id", ambassadorData.id)
        .order("created_at", { ascending: false });

      if (referralsError) throw referralsError;
      setReferrals((referralsData || []) as Referral[]);

      // Fetch prize draw entries for this ambassador
      const { data: prizeData, error: prizeError } = await supabase
        .from("prize_draw_entries")
        .select("*")
        .eq("ambassador_id", user.id)
        .eq("status", "succeeded")
        .order("created_at", { ascending: false });

      if (prizeError) throw prizeError;
      setPrizeEntries((prizeData || []) as PrizeDrawEntry[]);

      // Calculate stats
      const totalReferrals = referralsData?.length || 0;
      const totalEarnings = referralsData?.reduce(
        (sum, r) => sum + Number(r.commission_amount),
        0
      ) || 0;
      const paidOut = referralsData
        ?.filter((r) => r.paid_out)
        .reduce((sum, r) => sum + Number(r.commission_amount), 0) || 0;
      const pending = totalEarnings - paidOut;

      // Calculate prize draw stats
      const totalPrizeEntries = prizeData?.reduce((sum, e) => sum + e.entries, 0) || 0;
      const prizeCommission = (prizeData?.reduce((sum, e) => sum + e.ambassador_share, 0) || 0) / 100;

      setStats({ totalReferrals, totalEarnings, paidOut, pending, totalPrizeEntries, prizeCommission });
    } catch (error) {
      console.error("Error fetching ambassador data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReferralCode = async (): Promise<ReferralCode | null> => {
    if (!ambassador) return null;

    try {
      // Generate code using database function
      const { data: codeResult, error: codeError } = await supabase.rpc(
        "generate_referral_code"
      );

      if (codeError) throw codeError;

      // Insert the new referral code
      const { data: newCode, error: insertError } = await supabase
        .from("referral_codes")
        .insert({
          code: codeResult,
          ambassador_id: ambassador.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setReferralCodes((prev) => [newCode as ReferralCode, ...prev]);
      return newCode as ReferralCode;
    } catch (error) {
      console.error("Error generating referral code:", error);
      return null;
    }
  };

  return {
    ambassador,
    referralCodes,
    referrals,
    prizeEntries,
    stats,
    loading,
    isAmbassador,
    generateReferralCode,
    refetch: fetchAmbassadorData,
  };
}

export default useAmbassador;
